import * as Sentry from '@sentry/browser';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { Loading, useBoolean } from '@betrybe/ada-react';
import BalanceLayout from '@/layouts/balanceLayout/BalanceLayout';
import {
  fetchBalance,
  useOverdueBankSlip,
  usePayment,
  useUpcomingBankSlip,
} from '@/services/balance';
import useEnrollment from '@/services/enrollment/useEnrollment';
import type { PaymentType } from '@/services/balance/usePayment';
import type { BankSlipType } from '@/services/balance/types';
import { formatToBRL } from '@/utils';
import {
  BalancePageContent,
  BalancePageShell,
  BillingGrid,
  BillingPanel,
  BillingPanelBody,
  BillingPanelHeader,
  BillingPanelMeta,
  BillingPanelSubtitle,
  BillingTabButton,
  BillingTabs,
  BillingPanelTitle,
  BottomDivider,
  CompactWarningCard,
  CompactWarningDescription,
  CompactWarningIcon,
  CompactWarningTextBlock,
  CompactWarningTitle,
  Disclaimer,
  HeaderDivider,
  HeaderSection,
  IssuedList,
  IssuedListDate,
  IssuedListDueIn,
  IssuedListInlineText,
  IssuedListItem,
  IssuedListText,
  IssuedListValue,
  LoadingWrapper,
  OverdueList,
  OverdueStatementAmount,
  OverdueStatementBreakdown,
  OverdueStatementCard,
  OverdueStatementHeader,
  OverdueStatementLabel,
  OverdueStatementMeta,
  OverdueStatementRow,
  OverdueStatementValue,
  PanelEmptyText,
  PaymentsEmptyState,
  PaymentsEmptyText,
  PaymentsWarningLink,
  SummaryCardActionRow,
  SummaryActionHelperText,
  SummaryCardActionButton,
  SummaryCard,
  SummaryCardLabel,
  SummaryCardNoteGrid,
  SummaryCardsGrid,
  SummaryCardValue,
  SummarySection,
  SummarySectionTitle,
  WelcomeDescription,
  WelcomeTitle,
} from './styles';
import '../../styles/components/pages/balance.css';
import IpcaAdjustmentModal from './components';

function PaymentHistoryEmptyIllustration(): ReactElement {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="128"
      viewBox="0 0 178 128"
      width="178"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        fill="#E7EDF7"
        height="58"
        rx="4"
        transform="rotate(-16 26 38)"
        width="34"
        x="26"
        y="38"
      />
      <rect
        fill="#D8E2F1"
        height="54"
        rx="4"
        transform="rotate(12 127 30)"
        width="32"
        x="127"
        y="30"
      />
      <rect fill="#F8B84D" height="12" rx="2" width="12" x="34" y="32" />
      <rect fill="#F8B84D" height="10" rx="2" width="10" x="134" y="24" />
      <rect
        fill="#F7FAFF"
        height="86"
        rx="10"
        stroke="#6D7F9C"
        strokeWidth="4"
        width="72"
        x="53"
        y="20"
      />
      <rect
        fill="#A7B5CB"
        height="18"
        rx="6"
        stroke="#6D7F9C"
        strokeWidth="4"
        width="30"
        x="74"
        y="12"
      />
      <path
        d="M75 48H103"
        stroke="#C4CEDD"
        strokeLinecap="round"
        strokeWidth="4"
      />
      <circle cx="78" cy="65" fill="#6D7F9C" r="4" />
      <circle cx="100" cy="65" fill="#6D7F9C" r="4" />
      <path
        d="M76 84C82.5 76 95.5 76 102 84"
        stroke="#6D7F9C"
        strokeLinecap="round"
        strokeWidth="4"
      />
      <path
        d="M67 28L73 34"
        stroke="#6D7F9C"
        strokeLinecap="round"
        strokeWidth="4"
      />
      <path
        d="M111 28L105 34"
        stroke="#6D7F9C"
        strokeLinecap="round"
        strokeWidth="4"
      />
    </svg>
  );
}

type BillingTab = 'overdue' | 'upcoming' | 'paid';
const billingTabsOrder: BillingTab[] = ['upcoming', 'overdue', 'paid'];

function Balance(): ReactElement {
  const { getBalanceData, getContractData } = fetchBalance();
  const { getOverdueBankSlips } = useOverdueBankSlip();
  const { getPayments } = usePayment();
  const { getUpcomingBankSlips } = useUpcomingBankSlip();
  const history = useHistory();
  const [openBalance, setOpenBalance] = useState('');
  const [paidBalance, setPaidBalance] = useState('');
  const [initialBalanceValue, setInitialBalanceValue] = useState('');
  const [contractId, setContractId] = useState('');
  const [enrollmentStatus, setEnrollmentStatus] = useState<
    string | undefined
  >();
  const [hasIpcaAdjustment, setHasIpcaAdjustment] = useState(false);
  const [enrollmentError, setEnrollmentError] = useState(false);
  const [overdueBankSlips, setOverdueBankSlips] = useState<BankSlipType[]>([]);
  const [payments, setPayments] = useState<PaymentType[]>([]);
  const [upcomingBankSlips, setUpcomingBankSlips] = useState<BankSlipType[]>(
    [],
  );
  const [activeBillingTab, setActiveBillingTab] =
    useState<BillingTab>('upcoming');
  const billingTabRefs = useRef<Record<BillingTab, HTMLButtonElement | null>>({
    upcoming: null,
    overdue: null,
    paid: null,
  });
  const [isLoading, { setValue: setIsLoadingContractId }] = useBoolean(false);
  const [isLoadingBalanceData, { setValue: setIsLoadingBalanceData }] =
    useBoolean(false);
  const [
    isLoadingOverdueBankSlips,
    { setValue: setIsLoadingOverdueBankSlips },
  ] = useBoolean(false);
  const [isLoadingPayments, { setValue: setIsLoadingPayments }] =
    useBoolean(false);
  const [
    isLoadingUpcomingBankSlips,
    { setValue: setIsLoadingUpcomingBankSlips },
  ] = useBoolean(false);
  const [
    {
      auth_identity_data: {
        data: { user },
      },
    },
  ] = useCookies();
  const hasStudentRole = user.roles.includes('student');
  const [modalOpened, { setTrue: openModal, setFalse: closeModal }] =
    useBoolean(false);
  const { getEnrollment } = useEnrollment();

  useEffect(() => {
    setIsLoadingContractId(true);
    setIsLoadingBalanceData(true);
    setIsLoadingOverdueBankSlips(true);
    setIsLoadingPayments(true);
    setIsLoadingUpcomingBankSlips(true);
    let isSubscribed = true;

    getContractData()
      .then((contractData) => {
        if (contractData) {
          if (isSubscribed) {
            setContractId(contractData.contractId);
            setHasIpcaAdjustment(contractData.hasIpcaAdjustment);
          }

          if (contractData.enrollmentId) {
            Sentry.addBreadcrumb({
              category: 'enrollment',
              message: 'Fetching enrollment status',
              data: { enrollmentId: contractData.enrollmentId },
            });

            getEnrollment(contractData.enrollmentId)
              .then((enrollmentRes) => {
                if (isSubscribed) {
                  setEnrollmentStatus(enrollmentRes.data.status);
                  Sentry.addBreadcrumb({
                    category: 'enrollment',
                    message: 'Enrollment status fetched',
                    data: {
                      enrollmentId: contractData.enrollmentId,
                      status: enrollmentRes.data.status,
                    },
                  });
                }
              })
              .catch((error) => {
                if (isSubscribed) setEnrollmentError(true);
                Sentry.captureException(error, {
                  contexts: {
                    enrollment: {
                      enrollmentId: contractData.enrollmentId,
                      contractId: contractData.contractId,
                    },
                  },
                  tags: {
                    feature: 'balance-page',
                    area: 'enrollment-status',
                  },
                  fingerprint: [
                    'balance-enrollment-status-fetch-failure',
                    contractData.enrollmentId,
                  ],
                  extra: {
                    message: 'Failed to fetch enrollment status',
                  },
                });
              });
          }

          getBalanceData(contractData.contractId)
            .then((balanceData) => {
              if (isSubscribed) {
                setOpenBalance(balanceData.open_balance);
                setPaidBalance(balanceData.total_paid);
                setInitialBalanceValue(balanceData.financing_initial_value);
              }
            })
            .finally(() => {
              if (isSubscribed) {
                setIsLoadingBalanceData(false);
              }
            });

          getOverdueBankSlips(contractData.contractId)
            .then((fetchedOverdueBankSlips) => {
              if (isSubscribed) {
                setOverdueBankSlips(fetchedOverdueBankSlips);
              }
            })
            .finally(() => {
              if (isSubscribed) {
                setIsLoadingOverdueBankSlips(false);
              }
            });

          getPayments(contractData.contractId)
            .then((fetchedPayments) => {
              if (isSubscribed) {
                setPayments(fetchedPayments);
              }
            })
            .finally(() => {
              if (isSubscribed) {
                setIsLoadingPayments(false);
              }
            });

          getUpcomingBankSlips(contractData.contractId)
            .then((fetchedUpcomingBankSlips) => {
              if (isSubscribed) {
                setUpcomingBankSlips(fetchedUpcomingBankSlips);
              }
            })
            .finally(() => {
              if (isSubscribed) {
                setIsLoadingUpcomingBankSlips(false);
              }
            });
        } else {
          if (isSubscribed) {
            setIsLoadingBalanceData(false);
            setIsLoadingOverdueBankSlips(false);
            setIsLoadingPayments(false);
            setIsLoadingUpcomingBankSlips(false);
          }

          Sentry.setUser({ id: contractId });
          Sentry.captureMessage(
            'This account does not have access to the payment-history page. This error occurred within the getContractData call',
          );

          history.push('/no-access');
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setIsLoadingContractId(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasStudentRole) {
      Sentry.setUser({ id: contractId });
      Sentry.captureMessage(
        'This account does not have access to the payment-history page. This user does not have the student role',
      );

      history.push('/no-access');
    }
  }, [hasStudentRole, contractId, history]);

  const handleDateDisplay = (dateString: string) => {
    const datePrefixMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (datePrefixMatch) {
      const [, year, month, day] = datePrefixMatch;

      return `${day}/${month}/${year}`;
    }

    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  };

  const getOverdueBankSlipTotal = (bankSlip: BankSlipType) =>
    (
      Number(bankSlip.bank_slip_value) +
      Number(bankSlip.penalty_simulation) +
      Number(bankSlip.fee_simulation)
    ).toFixed(2);

  // Intentionally sums only principal (`bank_slip_value`), excluding
  // penalties/fees to match the summary business rule.
  const getBankSlipsPrincipalTotal = (bankSlips: BankSlipType[]) =>
    bankSlips
      .reduce((accumulator, bankSlip) => {
        return accumulator + Number(bankSlip.bank_slip_value);
      }, 0)
      .toFixed(2);

  const sortedUpcomingBankSlips = useMemo(
    () =>
      [...upcomingBankSlips].sort(
        (firstBankSlip, secondBankSlip) =>
          new Date(firstBankSlip.due_date).getTime() -
          new Date(secondBankSlip.due_date).getTime(),
      ),
    [upcomingBankSlips],
  );

  const getDaysUntilDate = (dateString: string) => {
    const date = new Date(dateString);
    const dueDateInUTC = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
    );
    const today = new Date();
    const todayInUTC = Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
    );

    return Math.ceil((dueDateInUTC - todayInUTC) / 86400000);
  };

  const firstName = user.name ? user.name.split(' ')[0] : '';

  const handleBillingTabKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    currentTab: BillingTab,
  ) => {
    const currentIndex = billingTabsOrder.indexOf(currentTab);

    if (event.key === 'Home') {
      const firstTab = billingTabsOrder[0];

      if (!firstTab) {
        return;
      }

      event.preventDefault();
      setActiveBillingTab(firstTab);
      billingTabRefs.current[firstTab]?.focus();

      return;
    }

    if (event.key === 'End') {
      const lastTab = billingTabsOrder[billingTabsOrder.length - 1];

      if (!lastTab) {
        return;
      }

      event.preventDefault();
      setActiveBillingTab(lastTab);
      billingTabRefs.current[lastTab]?.focus();

      return;
    }

    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }

    event.preventDefault();
    const nextIndex =
      event.key === 'ArrowRight'
        ? (currentIndex + 1) % billingTabsOrder.length
        : (currentIndex - 1 + billingTabsOrder.length) %
          billingTabsOrder.length;
    const nextTab = billingTabsOrder[nextIndex];
    if (!nextTab) {
      return;
    }

    setActiveBillingTab(nextTab);
    billingTabRefs.current[nextTab]?.focus();
  };

  if (
    isLoading ||
    isLoadingBalanceData ||
    isLoadingOverdueBankSlips ||
    isLoadingPayments ||
    isLoadingUpcomingBankSlips
  ) {
    return (
      <LoadingWrapper>
        <Loading>Aguarde...</Loading>
      </LoadingWrapper>
    );
  }

  return (
    <BalanceLayout>
      {contractId && (
        <BalancePageShell
          data-enrollment-error={enrollmentError || undefined}
          data-status={enrollmentStatus || 'unknown'}
        >
          <BalancePageContent>
            <HeaderSection>
              <WelcomeTitle>
                {firstName ? `Olá, ${firstName} 👋` : 'Olá 👋'}
              </WelcomeTitle>
              <WelcomeDescription>
                Tudo sobre o seu contrato em um só lugar!
              </WelcomeDescription>
              <HeaderDivider />
            </HeaderSection>

            {enrollmentStatus !== 'expired' && (
              <>
                <SummarySection>
                  <SummarySectionTitle>Acompanhe seu MSC</SummarySectionTitle>
                  <SummaryCardsGrid>
                    <SummaryCard tone="unpaid">
                      <SummaryCardLabel>Saldo Total*</SummaryCardLabel>
                      <SummaryCardValue>
                        {formatToBRL(openBalance)}
                      </SummaryCardValue>
                      {hasIpcaAdjustment && (
                        <SummaryCardActionRow>
                          <SummaryCardActionButton
                            onClick={openModal}
                            type="button"
                          >
                            Ver Histórico de Reajustes&nbsp;&gt;
                          </SummaryCardActionButton>
                        </SummaryCardActionRow>
                      )}
                    </SummaryCard>
                    <SummaryCard tone="issued">
                      <SummaryCardLabel>Total a vencer</SummaryCardLabel>
                      <SummaryCardValue>
                        {formatToBRL(
                          getBankSlipsPrincipalTotal(upcomingBankSlips),
                        )}
                      </SummaryCardValue>
                    </SummaryCard>
                    <SummaryCard tone="overdue">
                      <SummaryCardLabel>Total Vencido</SummaryCardLabel>
                      <SummaryCardValue>
                        {formatToBRL(
                          getBankSlipsPrincipalTotal(overdueBankSlips),
                        )}
                      </SummaryCardValue>
                    </SummaryCard>
                    <SummaryCard tone="paid">
                      <SummaryCardLabel>Total Pago</SummaryCardLabel>
                      <SummaryCardValue>
                        {formatToBRL(paidBalance)}
                      </SummaryCardValue>
                    </SummaryCard>
                  </SummaryCardsGrid>
                  <SummaryCardNoteGrid>
                    <SummaryActionHelperText>
                      *O saldo total se refere ao montante a ser pago caso as
                      condições de pagamento do MSC sejam atingidas. Uma vez
                      atingido o prazo máximo contratual, caso ainda exista
                      saldo total, o mesmo se tornará inexigível.
                    </SummaryActionHelperText>
                  </SummaryCardNoteGrid>
                </SummarySection>
              </>
            )}

            <BottomDivider />

            <CompactWarningCard>
              <CompactWarningIcon aria-hidden="true">!</CompactWarningIcon>
              <CompactWarningTextBlock>
                <CompactWarningTitle>
                  Atenção: Os valores apresentados estão sujeitos a atualização
                  e podem não refletir imediatamente pagamentos ou alterações
                  recentes.
                </CompactWarningTitle>
                <CompactWarningDescription>
                  Para dúvidas, entre em contato com nossa equipe de cobrança
                  pelo{' '}
                  <PaymentsWarningLink
                    aria-label="Falar com a equipe de cobrança no WhatsApp"
                    href="https://wa.me/551140037609"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    WhatsApp
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 32 32"
                      version="1.1"
                    >
                      <path d="M26.576 5.363c-2.69-2.69-6.406-4.354-10.511-4.354-8.209 0-14.865 6.655-14.865 14.865 0 2.732 0.737 5.291 2.022 7.491l-0.038-0.070-2.109 7.702 7.879-2.067c2.051 1.139 4.498 1.809 7.102 1.809h0.006c8.209-0.003 14.862-6.659 14.862-14.868 0-4.103-1.662-7.817-4.349-10.507l0 0zM16.062 28.228h-0.005c-0 0-0.001 0-0.001 0-2.319 0-4.489-0.64-6.342-1.753l0.056 0.031-0.451-0.267-4.675 1.227 1.247-4.559-0.294-0.467c-1.185-1.862-1.889-4.131-1.889-6.565 0-6.822 5.531-12.353 12.353-12.353s12.353 5.531 12.353 12.353c0 6.822-5.53 12.353-12.353 12.353h-0zM22.838 18.977c-0.371-0.186-2.197-1.083-2.537-1.208-0.341-0.124-0.589-0.185-0.837 0.187-0.246 0.371-0.958 1.207-1.175 1.455-0.216 0.249-0.434 0.279-0.805 0.094-1.15-0.466-2.138-1.087-2.997-1.852l0.010 0.009c-0.799-0.74-1.484-1.587-2.037-2.521l-0.028-0.052c-0.216-0.371-0.023-0.572 0.162-0.757 0.167-0.166 0.372-0.434 0.557-0.65 0.146-0.179 0.271-0.384 0.366-0.604l0.006-0.017c0.043-0.087 0.068-0.188 0.068-0.296 0-0.131-0.037-0.253-0.101-0.357l0.002 0.003c-0.094-0.186-0.836-2.014-1.145-2.758-0.302-0.724-0.609-0.625-0.836-0.637-0.216-0.010-0.464-0.012-0.712-0.012-0.395 0.010-0.746 0.188-0.988 0.463l-0.001 0.002c-0.802 0.761-1.3 1.834-1.3 3.023 0 0.026 0 0.053 0.001 0.079l-0-0.004c0.131 1.467 0.681 2.784 1.527 3.857l-0.012-0.015c1.604 2.379 3.742 4.282 6.251 5.564l0.094 0.043c0.548 0.248 1.25 0.513 1.968 0.74l0.149 0.041c0.442 0.14 0.951 0.221 1.479 0.221 0.303 0 0.601-0.027 0.889-0.078l-0.031 0.004c1.069-0.223 1.956-0.868 2.497-1.749l0.009-0.017c0.165-0.366 0.261-0.793 0.261-1.242 0-0.185-0.016-0.366-0.047-0.542l0.003 0.019c-0.092-0.155-0.34-0.247-0.712-0.434z" />
                    </svg>
                  </PaymentsWarningLink>
                </CompactWarningDescription>
              </CompactWarningTextBlock>
            </CompactWarningCard>

            <BillingGrid>
              <BillingTabs aria-label="Seções de boletos" role="tablist">
                <BillingTabButton
                  aria-controls="billing-panel-upcoming"
                  aria-selected={activeBillingTab === 'upcoming'}
                  id="billing-tab-upcoming"
                  onClick={() => setActiveBillingTab('upcoming')}
                  onKeyDown={(event) =>
                    handleBillingTabKeyDown(event, 'upcoming')
                  }
                  ref={(element) => {
                    billingTabRefs.current.upcoming = element;
                  }}
                  role="tab"
                  tabIndex={activeBillingTab === 'upcoming' ? 0 : -1}
                  tone="issued"
                  type="button"
                >
                  Boletos a vencer
                </BillingTabButton>
                <BillingTabButton
                  aria-controls="billing-panel-overdue"
                  aria-selected={activeBillingTab === 'overdue'}
                  id="billing-tab-overdue"
                  onClick={() => setActiveBillingTab('overdue')}
                  onKeyDown={(event) =>
                    handleBillingTabKeyDown(event, 'overdue')
                  }
                  ref={(element) => {
                    billingTabRefs.current.overdue = element;
                  }}
                  role="tab"
                  tabIndex={activeBillingTab === 'overdue' ? 0 : -1}
                  tone="overdue"
                  type="button"
                >
                  Boletos vencidos
                </BillingTabButton>
                <BillingTabButton
                  aria-controls="billing-panel-paid"
                  aria-selected={activeBillingTab === 'paid'}
                  id="billing-tab-paid"
                  onClick={() => setActiveBillingTab('paid')}
                  onKeyDown={(event) => handleBillingTabKeyDown(event, 'paid')}
                  ref={(element) => {
                    billingTabRefs.current.paid = element;
                  }}
                  role="tab"
                  tabIndex={activeBillingTab === 'paid' ? 0 : -1}
                  tone="paid"
                  type="button"
                >
                  Boletos pagos
                </BillingTabButton>
              </BillingTabs>

              <BillingPanel
                aria-hidden={activeBillingTab !== 'overdue'}
                aria-labelledby="billing-tab-overdue"
                hidden={activeBillingTab !== 'overdue'}
                id="billing-panel-overdue"
                role="tabpanel"
              >
                <BillingPanelHeader>
                  <BillingPanelTitle>Boletos vencidos</BillingPanelTitle>
                  <BillingPanelMeta>
                    <BillingPanelSubtitle>
                      Correspondem aos valores vencidos e ainda não quitados,
                      sendo aplicáveis as regras contratuais de inadimplemento
                      financeiro.
                    </BillingPanelSubtitle>
                  </BillingPanelMeta>
                </BillingPanelHeader>
                {overdueBankSlips.length > 0 ? (
                  <OverdueList>
                    {overdueBankSlips.map((bankSlip) => (
                      <OverdueStatementCard
                        key={`${bankSlip.enrollment_id}-${bankSlip.due_date}-${bankSlip.issue_date}-overdue`}
                      >
                        <OverdueStatementHeader>
                          <OverdueStatementMeta>
                            Data de vencimento:{' '}
                            <strong>
                              {handleDateDisplay(bankSlip.due_date)}
                            </strong>
                          </OverdueStatementMeta>
                          <OverdueStatementMeta>
                            Total Estimado:{' '}
                            <OverdueStatementAmount>
                              {formatToBRL(getOverdueBankSlipTotal(bankSlip))}
                            </OverdueStatementAmount>
                          </OverdueStatementMeta>
                        </OverdueStatementHeader>
                        <OverdueStatementBreakdown>
                          <OverdueStatementRow>
                            <OverdueStatementLabel>
                              Valor do Boleto:
                            </OverdueStatementLabel>
                            <OverdueStatementValue>
                              {formatToBRL(bankSlip.bank_slip_value)}
                            </OverdueStatementValue>
                          </OverdueStatementRow>
                          <OverdueStatementRow>
                            <OverdueStatementLabel>
                              Simulação de Juros:
                            </OverdueStatementLabel>
                            <OverdueStatementValue>
                              {formatToBRL(bankSlip.penalty_simulation)}
                            </OverdueStatementValue>
                          </OverdueStatementRow>
                          <OverdueStatementRow>
                            <OverdueStatementLabel>
                              Simulação de Multa:
                            </OverdueStatementLabel>
                            <OverdueStatementValue>
                              {formatToBRL(bankSlip.fee_simulation)}
                            </OverdueStatementValue>
                          </OverdueStatementRow>
                          <OverdueStatementRow>
                            <OverdueStatementLabel>
                              Total:
                            </OverdueStatementLabel>
                            <OverdueStatementValue>
                              {formatToBRL(getOverdueBankSlipTotal(bankSlip))}
                            </OverdueStatementValue>
                          </OverdueStatementRow>
                        </OverdueStatementBreakdown>
                      </OverdueStatementCard>
                    ))}
                  </OverdueList>
                ) : (
                  <BillingPanelBody>
                    <PanelEmptyText>
                      Nenhum boleto vencido até o momento.
                    </PanelEmptyText>
                  </BillingPanelBody>
                )}
              </BillingPanel>

              <BillingPanel
                aria-hidden={activeBillingTab !== 'upcoming'}
                aria-labelledby="billing-tab-upcoming"
                hidden={activeBillingTab !== 'upcoming'}
                id="billing-panel-upcoming"
                role="tabpanel"
              >
                <BillingPanelHeader>
                  <BillingPanelTitle>Boletos a vencer</BillingPanelTitle>
                  <BillingPanelMeta>
                    <BillingPanelSubtitle>
                      Correspondem aos valores com vencimento futuro, a partir
                      das condições de pagamento do MSC já verificadas em meses
                      específicos.
                    </BillingPanelSubtitle>
                  </BillingPanelMeta>
                </BillingPanelHeader>
                <BillingPanelBody>
                  {upcomingBankSlips.length > 0 ? (
                    <IssuedList>
                      {sortedUpcomingBankSlips.map((bankSlip) => {
                        const daysUntilDueDate = getDaysUntilDate(
                          bankSlip.due_date,
                        );
                        const isDueToday = daysUntilDueDate === 0;
                        const isOverdue = daysUntilDueDate < 0;

                        return (
                          <IssuedListItem
                            key={`${bankSlip.enrollment_id}-${bankSlip.due_date}-${bankSlip.issue_date}-upcoming`}
                          >
                            <IssuedListInlineText>
                              <IssuedListDate>
                                {handleDateDisplay(bankSlip.due_date)}
                              </IssuedListDate>
                              <IssuedListDueIn highlight={isDueToday}>
                                {isDueToday && 'Vence hoje'}
                                {isOverdue &&
                                  `Vencido há ${Math.abs(daysUntilDueDate)} ${
                                    Math.abs(daysUntilDueDate) === 1
                                      ? 'dia'
                                      : 'dias'
                                  }`}
                                {!isDueToday &&
                                  !isOverdue &&
                                  `Vence em ${daysUntilDueDate} ${
                                    daysUntilDueDate === 1 ? 'dia' : 'dias'
                                  }`}
                              </IssuedListDueIn>
                            </IssuedListInlineText>
                            <IssuedListValue>
                              {formatToBRL(bankSlip.bank_slip_value)}
                            </IssuedListValue>
                          </IssuedListItem>
                        );
                      })}
                    </IssuedList>
                  ) : (
                    <PanelEmptyText>
                      Nenhum boleto emitido até o momento.
                    </PanelEmptyText>
                  )}
                </BillingPanelBody>
              </BillingPanel>

              <BillingPanel
                aria-hidden={activeBillingTab !== 'paid'}
                aria-labelledby="billing-tab-paid"
                hidden={activeBillingTab !== 'paid'}
                id="billing-panel-paid"
                role="tabpanel"
              >
                <BillingPanelHeader>
                  <BillingPanelTitle>Boletos pagos</BillingPanelTitle>
                  <BillingPanelMeta>
                    <BillingPanelSubtitle>
                      Correspondem aos valores já quitados.
                    </BillingPanelSubtitle>
                  </BillingPanelMeta>
                </BillingPanelHeader>
                <BillingPanelBody>
                  {payments.length > 0 ? (
                    <IssuedList>
                      {payments.map((payment) => (
                        <IssuedListItem key={payment.id}>
                          <IssuedListText>
                            <IssuedListDueIn>Data de pagamento</IssuedListDueIn>
                            <IssuedListDate>
                              {handleDateDisplay(payment.payment_date)}
                            </IssuedListDate>
                          </IssuedListText>
                          <IssuedListValue tone="paid">
                            {formatToBRL(payment.paid_value)}
                          </IssuedListValue>
                        </IssuedListItem>
                      ))}
                    </IssuedList>
                  ) : (
                    <PaymentsEmptyState>
                      <PaymentHistoryEmptyIllustration />
                      <PaymentsEmptyText>
                        Nenhum pagamento realizado até o momento.
                      </PaymentsEmptyText>
                    </PaymentsEmptyState>
                  )}
                </BillingPanelBody>
              </BillingPanel>
            </BillingGrid>

            <Disclaimer>
              Esta é uma versão beta. Os valores mostrados podem não representar
              a realidade em todas as situações. Encontrou algum erro?{' '}
              <a href="mailto:msc@betrybe.com">msc@betrybe.com</a>
            </Disclaimer>
          </BalancePageContent>
        </BalancePageShell>
      )}
      <IpcaAdjustmentModal
        closeModal={closeModal}
        contractId={contractId}
        initialBalanceValue={initialBalanceValue}
        modalOpened={modalOpened}
      />
    </BalanceLayout>
  );
}

export default Balance;
